import React, { useState, useRef, useEffect } from "react";
import { usePatients } from "../hooks/usePatients";

/* ── Avatar initials ─────────────────────────────────────────────────────── */
function Avatar({ name = "", size = 32 }) {
  const initials = name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase();
  const colors = [
    ["#EEF2FF","#4F46E5"], ["#ECFDF5","#059669"],
    ["#FFF7ED","#D97706"], ["#FDF2F8","#9D174D"],
    ["#EFF6FF","#1D4ED8"], ["#F0FDF4","#15803D"],
  ];
  const [bg, text] = colors[(name.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: bg, color: text,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 800,
      fontFamily: "'Syne', sans-serif", flexShrink: 0,
      border: `1.5px solid ${text}22`,
    }}>
      {initials || "?"}
    </div>
  );
}

/* ── Pagination ──────────────────────────────────────────────────────────── */
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 1;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 4, padding: "10px 0 4px", borderTop: "1px solid rgba(99,102,241,.08)",
    }}>
      <PagBtn label="‹" disabled={page <= 1}      onClick={() => onPage(page - 1)} />
      {pages.map((p, i) =>
        p === "…"
          ? <span key={i} style={{ color: "#9B99C4", fontSize: 12, padding: "0 2px" }}>…</span>
          : <PagBtn key={p} label={p} active={p === page} onClick={() => onPage(p)} />
      )}
      <PagBtn label="›" disabled={page >= totalPages} onClick={() => onPage(page + 1)} />
    </div>
  );
}

function PagBtn({ label, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: 30, height: 30, borderRadius: 8, border: "none", cursor: disabled ? "default" : "pointer",
      background: active ? "linear-gradient(135deg,#818CF8,#4F46E5)" : "transparent",
      color: active ? "#fff" : disabled ? "#C4C3E0" : "#5B5A8A",
      fontSize: 12, fontWeight: active ? 700 : 500,
      fontFamily: "'DM Sans', sans-serif",
      transition: "all .15s",
      boxShadow: active ? "0 2px 8px rgba(79,70,229,.35)" : "none",
    }}>
      {label}
    </button>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function PatientSelector({ selectedPatient, onSelect }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const {
    query, patients, page, totalPages, total,
    loading, error, handleSearch, goToPage,
  } = usePatients();

  /* close on outside click */
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectPatient = (p) => {
    onSelect(p);
    setOpen(false);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onSelect(null);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>

      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: selectedPatient ? "5px 10px 5px 6px" : "5px 14px",
          borderRadius: 999, border: "none", cursor: "pointer",
          background: selectedPatient
            ? "linear-gradient(135deg,#818CF8,#4F46E5)"
            : "rgba(99,102,241,.1)",
          color: selectedPatient ? "#fff" : "#4F46E5",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 12, fontWeight: 600,
          transition: "all .18s",
          boxShadow: selectedPatient
            ? "0 2px 10px rgba(79,70,229,.4)"
            : "none",
          whiteSpace: "nowrap",
        }}
      >
        {selectedPatient ? (
          <>
            <Avatar name={selectedPatient.user_profile?.name || "?"} size={22} />
            <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
              {selectedPatient.user_profile?.name || "Patient"}
            </span>
            <span
              onClick={clearSelection}
              style={{
                marginLeft: 2, opacity: .75, fontSize: 14, lineHeight: 1,
                cursor: "pointer", padding: "0 2px",
              }}
            >×</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 14 }}>👤</span>
            All Users
            <span style={{ fontSize: 10, opacity: .7 }}>▾</span>
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 340, maxHeight: 480,
          background: "#fff", borderRadius: 16,
          border: "1px solid rgba(99,102,241,.14)",
          boxShadow: "0 8px 32px rgba(79,70,229,.18), 0 2px 8px rgba(79,70,229,.10)",
          zIndex: 9999, overflow: "hidden",
          display: "flex", flexDirection: "column",
          animation: "ua-drop .18s ease",
        }}>

          {/* Search bar */}
          <div style={{ padding: "12px 12px 8px" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#F8F7FF", border: "1px solid rgba(99,102,241,.14)",
              borderRadius: 10, padding: "7px 12px",
            }}>
              <span style={{ fontSize: 14, opacity: .5 }}>🔍</span>
              <input
                autoFocus
                value={query}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by name..."
                style={{
                  border: "none", background: "transparent", outline: "none",
                  fontSize: 13, color: "#0F0C29", width: "100%",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
              {query && (
                <span
                  onClick={() => handleSearch("")}
                  style={{ fontSize: 14, cursor: "pointer", opacity: .5 }}
                >×</span>
              )}
            </div>
          </div>

          {/* Meta row */}
          <div style={{
            padding: "0 14px 8px",
            fontSize: 11, color: "#9B99C4", fontWeight: 500,
          }}>
            {loading ? "Searching…" : error ? `Error: ${error}` : `${total} patient${total !== 1 ? "s" : ""}`}
          </div>

          {/* All patients option */}
          <div
            onClick={() => selectPatient(null)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 14px", cursor: "pointer",
              background: !selectedPatient ? "rgba(99,102,241,.07)" : "transparent",
              borderBottom: "1px solid rgba(99,102,241,.07)",
              transition: "background .12s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,.06)"}
            onMouseLeave={e => e.currentTarget.style.background = !selectedPatient ? "rgba(99,102,241,.07)" : "transparent"}
          >
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "linear-gradient(135deg,#EEF2FF,#E0E7FF)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, flexShrink: 0,
            }}>👥</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F0C29" }}>All Users</div>
              <div style={{ fontSize: 11, color: "#9B99C4" }}>Aggregate data — everyone</div>
            </div>
            {!selectedPatient && (
              <div style={{ marginLeft: "auto", color: "#4F46E5", fontSize: 16 }}>✓</div>
            )}
          </div>

          {/* Patient list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && patients.length === 0 ? (
              <div style={{ padding: "24px 0", textAlign: "center" }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{
                    height: 52, margin: "6px 14px", borderRadius: 10,
                    background: "linear-gradient(90deg,#F3F1FF,#EBE8FF 40%,rgba(200,196,255,.4) 50%,#EBE8FF 60%,#F3F1FF)",
                    backgroundSize: "600px 100%",
                    animation: "ua-shim 1.5s infinite linear",
                  }} />
                ))}
              </div>
            ) : patients.length === 0 ? (
              <div style={{ padding: "28px 0", textAlign: "center", color: "#9B99C4", fontSize: 13 }}>
                No patients found
              </div>
            ) : (
              patients.map((p) => {
                const isSelected = selectedPatient?.owner_id === p.owner_id;
                const profile = p.user_profile || {};
                return (
                  <div
                    key={p.owner_id}
                    onClick={() => selectPatient(p)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "9px 14px", cursor: "pointer",
                      background: isSelected ? "rgba(99,102,241,.07)" : "transparent",
                      transition: "background .12s",
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(99,102,241,.04)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isSelected ? "rgba(99,102,241,.07)" : "transparent"; }}
                  >
                    <Avatar name={profile.name || "?"} size={34} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: "#0F0C29",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {profile.name || "Unknown"}
                      </div>
                      <div style={{ fontSize: 11, color: "#9B99C4", marginTop: 1 }}>
                        {[profile.age && `${profile.age}y`, profile.gender].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    {isSelected && (
                      <div style={{ color: "#4F46E5", fontSize: 16, flexShrink: 0 }}>✓</div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          <div style={{ padding: "0 8px 8px" }}>
            <Pagination page={page} totalPages={totalPages} onPage={goToPage} />
          </div>
        </div>
      )}
    </div>
  );
}